import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Button,
  Container,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  IconButton,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { collectionsApi, llmApi } from "../services/rag-service";
import CollectionCard from "../components/CollectionCard";
import CollectionSkeletonCard from "../components/skeletons/CollectionSkeletonCard";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import {
  useProjects,
  validateSelectedProjects,
} from "../contexts/ProjectContext";
import { PROJECT_SELECTION_CHANGED_EVENT } from "../components/ProjectSwitcher";
import { useTranslation } from "react-i18next";

const DocumentCollectionsPage = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [newCollectionDescription, setNewCollectionDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [embeddingModels, setEmbeddingModels] = useState([]);
  const [selectedEmbeddingModel, setSelectedEmbeddingModel] = useState("");
  const [loadingEmbeddingModels, setLoadingEmbeddingModels] = useState(false);
  const [selectedProjectForCollection, setSelectedProjectForCollection] =
    useState("");
  const [chunkSize, setChunkSize] = useState(1000);
  const [chunkOverlap, setChunkOverlap] = useState(100);

  // New state for deletion
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState(null);
  const [deletingCollection, setDeletingCollection] = useState(false);

  const navigate = useNavigate();
  const { selectedProjects, availableProjects } = useProjects();
  const { t } = useTranslation();

  // Use utility function to ensure we always have valid arrays with stable references
  const safeSelectedProjects = useMemo(
    () => validateSelectedProjects(selectedProjects),
    [selectedProjects],
  );
  const safeAvailableProjects = useMemo(
    () => (Array.isArray(availableProjects) ? availableProjects : []),
    [availableProjects],
  );

  // Filter out 'all' from selected projects and get actual project data
  const getActiveProjects = useCallback(() => {
    if (safeSelectedProjects.includes("all")) {
      return safeAvailableProjects;
    }
    return safeAvailableProjects.filter((project) =>
      safeSelectedProjects.includes(project.id),
    );
  }, [safeSelectedProjects, safeAvailableProjects]);

  const fetchCollections = useCallback(
    async (search = "") => {
      try {
        setLoading(true);
        const data = await collectionsApi.getCollections({
          search,
          project_ids: safeSelectedProjects,
        });
        setCollections(data);
        setError(null);
      } catch (err) {
        console.error("Error fetching collections:", err);
        setError(t("documentCollections.failedToLoadCollectionsLong"));
        toast.error(t("documentCollections.failedToLoadCollections"));
      } finally {
        setLoading(false);
      }
    },
    [safeSelectedProjects],
  );

  // Fetch embedding models
  const fetchEmbeddingModels = async () => {
    try {
      setLoadingEmbeddingModels(true);
      const data = await llmApi.getLLMConfigs({
        type: "embedding",
      });
      setEmbeddingModels(data.items);

      // If models are available, select the first one as default
      if (data.length > 0) {
        setSelectedEmbeddingModel(data[0].id);
      }
    } catch (err) {
      console.error("Error fetching embedding models:", err);
      toast.error(t("documentCollections.failedToLoadEmbeddingModels"));
    } finally {
      setLoadingEmbeddingModels(false);
    }
  };

  // Initial load of collections
  useEffect(() => {
    fetchCollections(searchTerm);
  }, [fetchCollections, searchTerm]);

  // Add listener for project selection changes
  useEffect(() => {
    const handleProjectChange = () => {
      fetchCollections(searchTerm);
    };

    // Add event listener
    window.addEventListener(
      PROJECT_SELECTION_CHANGED_EVENT,
      handleProjectChange,
    );

    // Clean up
    return () => {
      window.removeEventListener(
        PROJECT_SELECTION_CHANGED_EVENT,
        handleProjectChange,
      );
    };
  }, [fetchCollections, searchTerm]);

  // Set default selected project when opening create modal
  useEffect(() => {
    if (isCreateModalOpen) {
      fetchEmbeddingModels();

      const activeProjects = getActiveProjects();
      // Set default project selection
      if (activeProjects.length === 1) {
        setSelectedProjectForCollection(activeProjects[0].id);
      } else if (activeProjects.length > 1) {
        // If multiple projects, default to first one
        setSelectedProjectForCollection(activeProjects[0].id);
      } else {
        setSelectedProjectForCollection("");
      }
    }
  }, [isCreateModalOpen, getActiveProjects]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCollections(searchTerm);
  };

  const handleCreateCollection = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Validate project selection
    const activeProjects = getActiveProjects();
    if (activeProjects.length === 0) {
      toast.error(t("documentCollections.pleaseCreateOrSelectProject"));
      setSubmitting(false);
      return;
    }

    if (!selectedProjectForCollection) {
      toast.error(t("documentCollections.pleaseSelectProject"));
      setSubmitting(false);
      return;
    }

    try {
      await collectionsApi.createCollection({
        name: newCollectionName,
        description: newCollectionDescription,
        llm_config_id: selectedEmbeddingModel,
        project_id: selectedProjectForCollection, // Add project_id to the payload
        chunk_size: chunkSize,
        chunk_overlap: chunkOverlap,
      });

      // Refresh collections list
      fetchCollections(searchTerm);

      // Close modal and reset form
      setIsCreateModalOpen(false);
      setNewCollectionName("");
      setNewCollectionDescription("");
      setSelectedEmbeddingModel("");
      setSelectedProjectForCollection("");
      setChunkSize(1000);
      setChunkOverlap(100);

      toast.success(t("documentCollections.collectionCreatedSuccess"));
    } catch (err) {
      console.error("Error creating collection:", err);
      setError(t("documentCollections.failedToCreateCollection"));
      toast.error(t("documentCollections.failedToCreateCollection"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCollectionClick = (collectionId) => {
    navigate(`/collections/${collectionId}`);
  };

  // Handle deletion confirmation dialog
  const handleDeleteClick = (collectionId, collectionName, access_level) => {
    // Only allow delete for collections where user has admin access
    if (access_level !== "admin") {
      toast.error(t("documentCollections.noDeletePermission"));
      return;
    }

    setCollectionToDelete({ id: collectionId, name: collectionName });
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCollectionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!collectionToDelete) return;

    setDeletingCollection(true);
    try {
      await collectionsApi.deleteCollection(collectionToDelete.id);
      // Refresh collections list
      fetchCollections(searchTerm);
      toast.success(
        t("documentCollections.collectionDeletedSuccess", {
          name: collectionToDelete.name,
        }),
      );
    } catch (err) {
      console.error("Error deleting collection:", err);
      toast.error(`Failed to delete collection: ${err.message}`);
    } finally {
      setDeletingCollection(false);
      setDeleteDialogOpen(false);
      setCollectionToDelete(null);
    }
  };

  // Get the list of active projects for the dropdown
  const activeProjects = getActiveProjects();
  const needsProjectSelection = activeProjects.length > 1;

  return (
    <DashboardLayout title={t("documentCollections.title")}>
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          {/* Header with Create Button */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <Typography variant="h4" component="h1" fontWeight="bold">
                  {t("documentCollections.heading")}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={4}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const activeProjects = getActiveProjects();
                    if (activeProjects.length === 0) {
                      toast.error(
                        t("documentCollections.pleaseCreateOrSelectProject"),
                      );
                      return;
                    }
                    setIsCreateModalOpen(true);
                  }}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: "180px" },
                  }}
                >
                  {t("documentCollections.createCollection")}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Search Box */}
          <Paper
            component="form"
            onSubmit={handleSearch}
            sx={{ p: { xs: 1, sm: 2 }, mb: 4 }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={t("documentCollections.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm="auto">
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: "100px" },
                  }}
                >
                  {t("common.search")}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
          )}

          {/* Collections Grid */}
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {loading ? (
              // Display skeleton loaders while fetching data
              Array.from({ length: 6 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
                  <CollectionSkeletonCard />
                </Grid>
              ))
            ) : collections.length > 0 ? (
              // Display actual collection cards once data is loaded
              collections.map((collection) => (
                <Grid item xs={12} sm={6} md={4} key={collection.id}>
                  <CollectionCard
                    collection={collection}
                    onClick={(id) => handleCollectionClick(id)}
                    onDelete={(id, name, access_level) =>
                      handleDeleteClick(id, name, access_level)
                    }
                  />
                </Grid>
              ))
            ) : (
              // Display a message when no collections are available
              <Grid item xs={12}>
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    {t("documentCollections.noCollectionsFound")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {t("documentCollections.createFirstCollection")}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Create Collection Dialog */}
        <Dialog
          open={isCreateModalOpen}
          onClose={() => {
            if (!submitting) {
              setIsCreateModalOpen(false);
              // Reset form values when closing without creating
              setNewCollectionName("");
              setNewCollectionDescription("");
              setSelectedEmbeddingModel("");
              setSelectedProjectForCollection("");
              setChunkSize(1000);
              setChunkOverlap(100);
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {t("documentCollections.createNewCollection")}
            <IconButton
              aria-label="close"
              onClick={() => {
                setIsCreateModalOpen(false);
                // Reset form values when closing without creating
                setNewCollectionName("");
                setNewCollectionDescription("");
                setSelectedEmbeddingModel("");
                setSelectedProjectForCollection("");
                setChunkSize(1000);
                setChunkOverlap(100);
              }}
              disabled={submitting}
              sx={{
                position: "absolute",
                insetInlineEnd: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Box
              component="form"
              onSubmit={handleCreateCollection}
              sx={{ mt: 1 }}
            >
              {getActiveProjects().length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t("documentCollections.noProjectsAvailable")}
                </Alert>
              )}
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label={t("documentCollections.collectionName")}
                name="name"
                autoFocus
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                disabled={submitting}
              />

              <TextField
                margin="normal"
                fullWidth
                id="description"
                label={t("common.description")}
                name="description"
                multiline
                rows={3}
                value={newCollectionDescription}
                onChange={(e) => setNewCollectionDescription(e.target.value)}
                disabled={submitting}
              />

              {/* Project Selection Dropdown - Only shown when multiple projects are selected */}
              {needsProjectSelection && (
                <FormControl
                  fullWidth
                  margin="normal"
                  required
                  disabled={submitting}
                >
                  <InputLabel id="project-select-label">
                    {t("flows.project")}
                  </InputLabel>
                  <Select
                    labelId="project-select-label"
                    id="project-select"
                    value={selectedProjectForCollection}
                    label={t("common.project")}
                    onChange={(e) =>
                      setSelectedProjectForCollection(e.target.value)
                    }
                  >
                    {activeProjects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {t("documentCollections.selectProjectHelper")}
                  </FormHelperText>
                </FormControl>
              )}

              <Divider sx={{ my: 2 }} />

              <FormControl
                fullWidth
                margin="normal"
                required
                disabled={submitting}
              >
                <InputLabel id="embedding-model-label">
                  {t("documentCollections.embeddingModel")}
                </InputLabel>
                <Select
                  labelId="embedding-model-label"
                  id="embedding-model"
                  value={selectedEmbeddingModel}
                  label={t("documentCollections.embeddingModel")}
                  onChange={(e) => setSelectedEmbeddingModel(e.target.value)}
                  disabled={submitting || loadingEmbeddingModels}
                  startAdornment={
                    loadingEmbeddingModels ? (
                      <InputAdornment position="start">
                        <CircularProgress size={20} color="inherit" />
                      </InputAdornment>
                    ) : null
                  }
                >
                  {loadingEmbeddingModels ? (
                    <MenuItem disabled value="">
                      <em>{t("documentCollections.loadingEmbeddingModels")}</em>
                    </MenuItem>
                  ) : embeddingModels.length > 0 ? (
                    embeddingModels.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.name} ({model.provider})
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      <em>{t("documentCollections.noEmbeddingModels")}</em>
                    </MenuItem>
                  )}
                </Select>
                <FormHelperText>
                  {t("documentCollections.embeddingModelHelper")}
                </FormHelperText>
              </FormControl>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 1 }}>
                {t("documentCollections.documentProcessingSettings")}
              </Typography>

              <TextField
                margin="normal"
                fullWidth
                id="chunk-size"
                label={t("documentCollections.chunkSize")}
                name="chunk-size"
                type="number"
                value={chunkSize}
                onChange={(e) => setChunkSize(Number(e.target.value))}
                disabled={submitting}
                inputProps={{ min: 100 }}
                error={chunkSize < 100}
                helperText={
                  chunkSize < 100
                    ? t("documentCollections.chunkSizeMinError")
                    : t("documentCollections.chunkSizeHelper")
                }
              />

              <TextField
                margin="normal"
                fullWidth
                id="chunk-overlap"
                label={t("documentCollections.chunkOverlap")}
                name="chunk-overlap"
                type="number"
                value={chunkOverlap}
                onChange={(e) => setChunkOverlap(Number(e.target.value))}
                disabled={submitting}
                inputProps={{ min: 0 }}
                error={chunkOverlap < 0 || chunkOverlap >= chunkSize}
                helperText={
                  chunkOverlap < 0
                    ? t("documentCollections.chunkOverlapNegativeError")
                    : chunkOverlap >= chunkSize
                      ? t("documentCollections.chunkOverlapTooLargeError")
                      : t("documentCollections.chunkOverlapHelper")
                }
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => {
                setIsCreateModalOpen(false);
                // Reset form values when closing without creating
                setNewCollectionName("");
                setNewCollectionDescription("");
                setSelectedEmbeddingModel("");
                setSelectedProjectForCollection("");
                setChunkSize(1000);
                setChunkOverlap(100);
              }}
              disabled={submitting}
              color="secondary"
              variant="text"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleCreateCollection}
              disabled={
                submitting ||
                !newCollectionName ||
                (embeddingModels.length > 0 && !selectedEmbeddingModel) ||
                !selectedProjectForCollection ||
                chunkSize < 100 ||
                chunkOverlap < 0 ||
                chunkOverlap >= chunkSize
              }
              variant="contained"
              color="primary"
            >
              {submitting ? t("common.creating") : t("common.create")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
          <DialogTitle>{t("documentCollections.deleteCollection")}</DialogTitle>
          <DialogContent>
            <Typography>
              {t("documentCollections.deleteCollectionConfirm", {
                name: collectionToDelete?.name,
              })}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={handleCloseDeleteDialog}
              disabled={deletingCollection}
              color="secondary"
              variant="text"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              disabled={deletingCollection}
              startIcon={
                deletingCollection ? (
                  <CircularProgress size={20} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              {deletingCollection ? t("common.deleting") : t("common.delete")}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default DocumentCollectionsPage;
